import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';

const AssistantStep = ({ onNext, onBack }) => {
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [licenceNo, setLicenceNo] = useState(null);

  useEffect(() => {
    const fetchAssistants = async () => {
      const apiUrl = `https://portal.lsb.go.ke/account/assistant_details/`;
      const accessToken = localStorage.getItem("accessToken");

      try {
        setLoading(true);
        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch assistant details.");

        const data = await response.json();

        // --- Begin logging actual network data format ---
        if (Array.isArray(data)) {
          if (data.length === 0) {
            console.log("Fetched assistants: []");
          } else {
            data.forEach((assistant, idx) => {
              console.log(`Fetched assistant #${idx + 1}:`, {
                assistant_id: assistant.assistant_id,
                assistant_no: assistant.assistant_no,
                name: assistant.name,
                id_number: assistant.id_number,
                email: assistant.email,
                phone_number: assistant.phone_number,
                supervisor: assistant.supervisor,
                step_2: assistant.step_2,
                assistant_number: assistant.assistant_number,
              });
            });
          }
        } else if (data && typeof data === "object" && Object.keys(data).length > 0) {
          // Legacy/fallback object
          console.log("Fetched assistant [object]:", {
            assistant_id: data.assistant_id,
            assistant_no: data.assistant_no,
            name: data.name,
            id_number: data.id_number,
            email: data.email,
            phone_number: data.phone_number,
            supervisor: data.supervisor,
            step_2: data.step_2,
            assistant_number: data.assistant_number,
          });
        } else {
          console.log("Fetched assistants: [] (empty object/dataset)");
        }
        // --- End logging ---

        // Robustly extract assistants list and licence/supervisor ID
        let assistantsList = [];
        let extractedLicenceNo = null;

        if (Array.isArray(data)) {
          assistantsList = data;
          if (data.length > 0) {
            extractedLicenceNo =
              data[0].supervisor ||
              data[0].licence_no ||
              data[0].id ||
              null;
          }
        } else if (data && typeof data === "object") {
          // Format: {"assistants":[], "license_no":"299"}
          if (Array.isArray(data.assistants)) {
            assistantsList = data.assistants;
          } else if (data.assistant_id || data.name) {
            // It's a single assistant object
            assistantsList = [data];
          }

          // Extract ID from license_no, supervisor, or id
          extractedLicenceNo =
            data.license_no ||
            data.supervisor ||
            data.licence_no ||
            data.id ||
            (assistantsList.length > 0
              ? assistantsList[0].supervisor ||
              assistantsList[0].licence_no ||
              assistantsList[0].id
              : null);
        }

        setAssistants(assistantsList);
        setLicenceNo(extractedLicenceNo);
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: error.message,
          icon: "error",
          confirmButtonText: "Retry",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAssistants();
  }, []);

  const handleInputChange = (index, field, value) => {
    setAssistants((prevAssistants) =>
      prevAssistants.map((assistant, i) =>
        i === index ? { ...assistant, [field]: value } : assistant
      )
    );
  };

  // Only require filling assistant details if assistants exist
  const handleNext = async () => {
    const apiUrl = `https://portal.lsb.go.ke/account/assistant_details/`;
    const accessToken = localStorage.getItem("accessToken");

    try {
      const payload = {
        step_2: true,
        id: licenceNo,
        assistant: [],
      };

      if (assistants.length > 0) {
        // Validate all have required fields
        const allRequirementsMet = assistants.every(
          (assistant) =>
            assistant.name &&
            assistant.id_number &&
            assistant.email &&
            assistant.phone_number
        );
        if (!allRequirementsMet) {
          throw new Error(
            "Please fill out all required fields for all assistants."
          );
        }

        payload.assistant = assistants.map((assistant) => ({
          assistant_id: assistant.assistant_id,
          assistant_no: assistant.assistant_no,
          name: assistant.name,
          id_number: assistant.id_number,
          email: assistant.email,
          phone_number: assistant.phone_number,
          supervisor: assistant.supervisor,
          step_2: true,
          assistant_number: assistant.assistant_number,
        }));
      }

      // Log payload to check what is being sent to the server
      console.log("Sending assistant payload:", payload);

      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload, null, 2),
      });

      if (!response.ok) {
        let errorMsg = "Failed to update assistant details.";
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorData.message || errorMsg;
        } catch (e) {
          /* ignore bad json */
        }
        throw new Error(errorMsg);
      }

      Swal.fire({
        title: "Success",
        text: "Assistant details updated successfully!",
        icon: "success",
        confirmButtonText: "OK",
      });

      onNext();
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.message,
        icon: "error",
        confirmButtonText: "Retry",
      });
    }
  };

  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    Swal.fire({
      title: 'Success',
      text: 'You have been logged out.',
      icon: 'success',
      width: 370,
    });
    navigate('/portal');
  };

  return (
    <div className="">
      <div className="flex justify-between items-center pb-6">
        <h3 className="font-bold text-[#3d0707] tracking-wide underline underline-offset-4 decoration-[#ebcab3]">
          Approved Assistants
        </h3>

        <button
          onClick={handleLogout}
          className="py-2 px-3 bg-red-600 hover:font-bold uppercase text-white flex gap-2 items-center rounded-md text-sm transition-transform duration-200 ease-in-out hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-red-400 shadow-sm hover:shadow-lg"
        >
          <span className="transition duration-200 ease-in-out group-hover:pr-1">Logout</span>
          <LogOut className="w-5 h-5 transition-transform duration-200 group-hover:rotate-[-15deg]" />
        </button>
      </div>

      <div className="mb-6 leading-tight">
        <p className="mb-1 font-medium">
          Review and update email/phone details of your approved assistants. Only correct the phone number and email addresses where necessary.
        </p>

        <p className="font-medium">
          <b>Note:</b> <b className="text-[#ff0000]">*</b> marks required entries to be filled.
        </p>
      </div>

      {/* Table Display */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-xl shadow border border-sky-100">
          <thead>
            <tr className="bg-black text-white">
              <th className="p-3 text-left font-bold uppercase border-b border-sky-100">
                Assistant No
              </th>
              <th className="p-3 text-left font-bold uppercase border-b border-sky-100">
                Name
              </th>
              <th className="p-3 text-left font-bold uppercase border-b border-sky-100">
                ID Number
              </th>
              <th className="p-3 text-left font-bold uppercase border-b border-sky-100">
                Email <span className="text-[#ff0000]">*</span>
              </th>
              <th className="p-3 text-left font-bold uppercase border-b border-sky-100">
                Phone Number <span className="text-[#ff0000]">*</span>
              </th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center py-10">
                  <svg
                    className="animate-spin h-8 w-8 text-sky-400 mx-auto"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    ></path>
                  </svg>
                </td>
              </tr>
            ) : assistants.length > 0 ? (
              assistants.map((assistant, index) => (
                <tr key={assistant.assistant_no || assistant.id || index} className="hover:bg-gray-50">
                  <td className="px-3 py-2 border-b border-sky-100 font-semibold text-gray-700 bg-gray-100">
                    {assistant.assistant_no}
                  </td>
                  <td className="px-3 py-2 border-b border-sky-100 font-semibold text-gray-700 bg-gray-100">
                    {assistant.name}
                  </td>
                  <td className="px-3 py-2 border-b border-sky-100 font-semibold text-gray-700 bg-gray-100">
                    {assistant.id_number}
                  </td>
                  <td className="px-3 py-2 border-b border-sky-100 bg-white">
                    <input
                      type="email"
                      value={assistant.email || ""}
                      placeholder="Assistant Email"
                      onChange={(e) => handleInputChange(index, "email", e.target.value)}
                      required
                      className="w-full rounded border border-gray-200 bg-white text-sm text-gray-700 shadow-sm font-semibold px-2 py-1 focus:outline-sky-400"
                    />
                  </td>
                  <td className="px-3 py-2 border-b border-sky-100 bg-white">
                    <input
                      type="number"
                      value={assistant.phone_number || ""}
                      placeholder="Assistant Contact"
                      onChange={(e) => handleInputChange(index, "phone_number", e.target.value)}
                      required
                      className="w-full rounded border border-gray-200 bg-white text-sm text-gray-700 shadow-sm font-semibold px-2 py-1 focus:outline-sky-400"
                    />
                  </td>
                </tr>
              ))
            ) : (
              // If no entries, show blank row
              <tr>
                <td colSpan={5} className="py-10 text-center text-gray-500">
                  No assistants available for your account.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pt-8 flex gap-3">
        <button type="button" onClick={onBack} className="button">
          <span className="button_lg">
            <span className="button_sl"></span>
            Back
          </span>
        </button>

        <button type="button" onClick={handleNext} className={`button`}>
          <span className="button_lg">
            <span className="button_sl"></span>
            Next
          </span>
        </button>
      </div>
    </div>
  );
};

export default AssistantStep;
